import React from 'react';
import { Bubble } from 'tea-component';
import SvgIcon from '@/pages/home/components/SvgIcon';
import useLanguage from '@op/hooks/useLanguage';
import RenderImg from '@op/components/RenderImg';
import { DefaultImg } from '@op/constants/defaultImg';
import type { TPermissionLevel } from '@op/services/report';
import { MAX_AVATAR_STACK, PERMISSION_LEVEL_ICON, VISIBLE_AVATAR_COUNT } from './constants';

type TAuthorizedUserInfo = {
  user_id: string;
  user_name?: string;
  profile_pic?: string;
};

export interface IPermissionCapsuleProps {
  record: {
    object_type?: string;
    object_id?: number;
    permission_level?: TPermissionLevel;
    authorized_user_count?: number;
    authorized_users_info?: TAuthorizedUserInfo[];
    can_edit_permission?: boolean;
  };
  editable?: boolean;
  onClick?: (
    record: IPermissionCapsuleProps['record'],
    event: React.MouseEvent,
  ) => void;
}

function renderOverflowPanel(
  restUsers: TAuthorizedUserInfo[],
  formatMessage: (params: { id: string; }) => string,
) {
  return (
    <div className="dbo-report-permission-capsule__overflow-panel">
      <div className="dbo-report-permission-capsule__overflow-title">
        {formatMessage({ id: 'Selected Users' })}
      </div>
      <div className="dbo-report-permission-capsule__overflow-list">
        {restUsers.map((user) => (
          <div
            key={user.user_id}
            className="dbo-report-permission-capsule__overflow-item"
          >
            <RenderImg
              coverImage={user.profile_pic || ''}
              defaultImage={DefaultImg.KOL}
              className="dbo-report-permission-capsule__overflow-avatar"
              mode="image"
            />
            <span className="dbo-report-permission-capsule__overflow-name">
              {user.user_name || user.user_id}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PermissionCapsule({
  record,
  editable = false,
  onClick,
}: IPermissionCapsuleProps) {
  const { formatMessage } = useLanguage();
  const level = record.permission_level;

  if (!record.object_type || record.object_id === null || record.object_id === undefined || !level) {
    return <span>-</span>;
  }

  const canClick = editable && record.can_edit_permission;

  const handleClick = (event: React.MouseEvent) => {
    if (!canClick) return;
    onClick?.(record, event);
  };

  let content: React.ReactNode = null;

  if (level === 'private') {
    content = (
      <>
        <SvgIcon
          type={PERMISSION_LEVEL_ICON.private}
          className="dbo-report-permission-capsule__icon"
        />
        <span>{formatMessage({ id: 'Only Me' })}</span>
      </>
    );
  } else if (level === 'public') {
    content = (
      <>
        <SvgIcon
          type={PERMISSION_LEVEL_ICON.public}
          className="dbo-report-permission-capsule__icon"
        />
        <span>{formatMessage({ id: 'All-1' })}</span>
      </>
    );
  } else {
    const users = record.authorized_users_info || [];
    const userCount = record.authorized_user_count ?? users.length;
    const hasOverflow = userCount > MAX_AVATAR_STACK;
    const visibleUsers = hasOverflow
      ? users.slice(0, VISIBLE_AVATAR_COUNT)
      : users;
    const overflowCount = userCount - VISIBLE_AVATAR_COUNT;

    content = (
      <Bubble
        content={renderOverflowPanel(users, formatMessage)}
        overlayClassName='dbo-report-permission-capsule__overflow-bubble'
      >
        <div className="dbo-report-permission-capsule__avatars">
          {visibleUsers.map((user) => (
            <RenderImg
              key={user.user_id}
              coverImage={user.profile_pic || ''}
              defaultImage={DefaultImg.KOL}
              className="dbo-report-permission-capsule__avatar"
              mode="image"
            />
          ))}
          {hasOverflow && (
            <span className="dbo-report-permission-capsule__avatar-more">
              +{overflowCount}
            </span>
          )}
        </div>
      </Bubble>
    );
  }

  return (
    <div className={`dbo-report-permission-capsule${canClick ? ' dbo-report-permission-capsule--clickable' : ''}`}
      onClick={handleClick}
    >
      {content}
    </div>
  );
}
